import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import Pagination from '@mui/material/Pagination';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PrintIcon from '@mui/icons-material/Print';
import TableViewIcon from '@mui/icons-material/TableView';
import { DataGrid, GridRowModes, useGridApiContext, useGridSelector } from '@mui/x-data-grid';
import { TablePagination } from '@mui/material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useSync } from '../contexts/SyncContext';
import { createApiClient } from '../services/apiClient';
import { fetchPatients, updatePatient, buildPatientUpdatePayload, createPatient, deletePatient } from '../services/patientService';
import PatientDetailDialog from '../components/PatientDetailDialog';
import PatientCard from '../components/PatientCard';
import PatientEditDialog from '../components/PatientEditDialog';
import {
  normalizePatient,
  formatFieldValue,
} from '../utils/patientNormalizer';
import { normalizeGreekSearch } from '../utils/greekUtils';
import { getPatientId } from '../utils/patientNormalizer';
import { formatDate } from '../utils/dateUtils';


const PatientsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { lastChange } = useSync();
  const lastFetchId = useRef(0);
  
  // Role-based permission helpers
  const isAdmin = user?.role === 'admin';
  // read-write and admin can add/edit patients
  const canWrite = user?.role === 'admin' || user?.role === 'read-write';
  // Load saved state from sessionStorage
  const savedState = useMemo(() => {
    if (typeof window !== 'undefined') {
      const saved = sessionStorage.getItem('patientsPageState');
      return saved ? JSON.parse(saved) : {};
    }
    return {};
  }, []);

  const [viewMode, setViewMode] = useState(savedState.viewMode || 'table');
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState(savedState.selectedPatientId || null);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState(savedState.searchTerm || '');
  const [cardPage, setCardPage] = useState(savedState.cardPage || 0);
  const [cardPageSize, setCardPageSize] = useState(savedState.cardPageSize || 10);
  const [detailPage, setDetailPage] = useState(savedState.detailPage || 0);
  const [detailPageSize, setDetailPageSize] = useState(savedState.detailPageSize || 25);
  const [paginationModel, setPaginationModel] = useState({
    page: savedState.tablePage || 0,
    pageSize: savedState.tablePageSize || 10,
  });
  const [rowModesModel, setRowModesModel] = useState({});
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchInputRef = useRef(null);

  // Handle inbound actions from Dashboard (Quick Actions)
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'new-patient') {
      setCreateOpen(true);
      // Clean up param
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
    } else if (action === 'new-visit') {
      // Focus search to find a patient to create a visit for
      // Note: searchInputRef is attached to the TextField below
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 500);
      // Clean up param
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const apiClient = useMemo(() => {
    if (!isAuthenticated) {
      return null;
    }
    // Get token from localStorage
    const token = sessionStorage.getItem('token');
    return createApiClient(token);
  }, [isAuthenticated]);

  const handleViewPatient = useCallback((patient) => {
    if (!patient) {
      return;
    }

    const patientId = patient.id ?? patient.__internalId ?? getPatientId(patient) ?? null;
    if (patientId) {
      navigate(`/patients/${patientId}`);
    }
  }, [navigate]);

  const handleCreateSubmit = async (values, formikHelpers) => {
    if (!apiClient) {
      formikHelpers.setStatus('You must be signed in to add patient records.');
      formikHelpers.setSubmitting(false);
      return;
    }

    const payload = buildPatientUpdatePayload(values);

    const attemptCreate = async (client) => {
      const created = await createPatient(client, payload);
      const normalized = normalizePatient(created, patients.length, created?.id ?? created?.ID_PT ?? undefined);
      setPatients((prev) => {
        const next = [normalized, ...prev];
        return next;
      });
      if (normalized?.id) {
        setSelectedPatientId(normalized.id);
      }
      setError('');
      return normalized;
    };

    setCreateSubmitting(true);
    formikHelpers.setStatus(undefined);
    setCreateError('');

    try {
      await attemptCreate(apiClient);
      setCreateOpen(false);
      formikHelpers.resetForm();
      await loadPatients();
    } catch (err) {
      if (err.response?.status === 401) {
        setCreateError('Session expired. Please log in again.');
        // TODO: Could implement logout redirect here
      } else {
        throw err;
      }

      const details = err.response?.data?.details;
      let message = err.response?.data?.error ?? err.message ?? 'Failed to add patient';
      
      if (err.response?.data?.error === 'Validation failed' && Array.isArray(details)) {
        const fieldErrors = details.map(d => `${d.path[d.path.length - 1]}: ${d.message}`).join(', ');
        message = `${t('Validation failed')}: ${fieldErrors}`;
      }

      formikHelpers.setStatus(message);
      setCreateError(message);
    } finally {
      setCreateSubmitting(false);
      formikHelpers.setSubmitting(false);
    }
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
  };

  const handleEditPatient = (patient) => {
    if (!patient) {
      return;
    }

    const patientId = patient.id ?? patient.__internalId ?? getPatientId(patient) ?? null;
    setSelectedPatientId(patientId);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
  };

  const handleCreateOpen = () => {
    if (!isAuthenticated) {
      return;
    }
    setCreateError('');
    setCreateOpen(true);
  };

  const handleCreateClose = () => {
    setCreateOpen(false);
  };

  const loadPatients = useCallback(async () => {
    if (!apiClient) return;

    // Surgical sync versioning: Prevent race conditions from overlapping fetches
    const currentFetchId = ++lastFetchId.current;
    
    setLoading(true);
    setError('');

    try {
      const pageSize = 100;
      // 1. Fetch the first page to get total count and initial batch
      const firstResult = await fetchPatients(apiClient, 1, pageSize);
      
      // Abandon if a newer fetch has already started
      if (currentFetchId !== lastFetchId.current) return;

      let allPatients = Array.isArray(firstResult.patients)
        ? firstResult.patients.map((item, index) => normalizePatient(item, index))
        : [];

      const totalRecords = firstResult.pagination?.total ?? allPatients.length;
      setTotalCount(totalRecords);

      const totalPages = firstResult.pagination?.totalPages
        ?? (totalRecords > 0 ? Math.ceil(totalRecords / pageSize) : 1);

      // 2. If multi-page, fetch remaining surgical records atomically
      if (totalPages > 1) {
        for (let page = 2; page <= totalPages; page += 1) {
          try {
            const pageResult = await fetchPatients(apiClient, page, pageSize);
            
            // Check version again after each async network hop
            if (currentFetchId !== lastFetchId.current) return;

            if (Array.isArray(pageResult.patients)) {
              const normalizedPage = pageResult.patients.map((item, index) => 
                normalizePatient(item, allPatients.length + index)
              );
              allPatients = [...allPatients, ...normalizedPage];
            }
          } catch (pageError) {
            console.error(`[Sync] Failed to fetch patients page ${page}:`, pageError);
            // Non-fatal, continue with what we have but log error
          }
        }
      }

      // 3. Final Surgical De-duplication: Ensure zero duplicate IDs in the clinical dashboard
      const seenIds = new Set();
      const uniquePatients = allPatients.filter(p => {
        const pId = p.id ?? p.__recordId;
        if (!pId || seenIds.has(pId)) return false;
        seenIds.add(pId);
        return true;
      });

      if (currentFetchId === lastFetchId.current) {
        setPatients(uniquePatients);
      }
    } catch (err) {
      if (currentFetchId !== lastFetchId.current) return;
      
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
        return;
      }
      setError(err.response?.data?.error ?? err.message ?? 'Failed to load patients');
    } finally {
      if (currentFetchId === lastFetchId.current) {
        setLoading(false);
      }
    }
  }, [apiClient]);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    const stateToSave = {
      viewMode,
      selectedPatientId,
      searchTerm,
      cardPage,
      cardPageSize,
      detailPage,
      detailPageSize,
      tablePage: paginationModel.page,
      tablePageSize: paginationModel.pageSize,
    };
    sessionStorage.setItem('patientsPageState', JSON.stringify(stateToSave));
  }, [viewMode, selectedPatientId, searchTerm, cardPage, cardPageSize, detailPage, detailPageSize, paginationModel]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  // Real-time surgical synchronization: Auto-refresh patient list when database changes occur on other workstations
  useEffect(() => {
    if (!lastChange) return;
    
    const { table, action } = lastChange;
    
    // Refresh the list if any patient-level change occurs
    if (table === 'patients') {
      console.log(`[Sync] Auto-refreshing patient list due to ${action} in ${table}`);
      loadPatients();
    }
  }, [lastChange, loadPatients]);

  const buildRowFromPatient = useCallback(
    (patient, index) => {
      // Extract and normalize fields with multiple possible sources
      const gender = formatFieldValue(patient.gender || patient.__gender || '');
      const dateOfBirth = formatDate(patient.dateOfBirth || patient.date_of_birth || patient.__dateOfBirth || '');
      const email = formatFieldValue(patient.email || patient.contact_email || patient.__email || '');
      
      // Create the raw object first with all possible fields
      const rawPatient = {
        ...patient,
        // Ensure these fields are always included in the raw object
        gender: gender,
        dateOfBirth: dateOfBirth,
        email: email,
        // Map all possible field names to consistent ones
        firstName: patient.firstName || patient.first_name || '',
        lastName: patient.lastName || patient.last_name || '',
        phone: patient.phone || patient.mobile || '',
        amka: patient.amka || '',
        address: patient.address || '',
        notes: patient.notes || '',
        diagnosis: patient.diagnosis || '',
      };
      
      return {
        id: patient.id ?? patient.__recordId ?? `patient-${index}`,
        idPt: formatFieldValue(patient.id ?? patient.__recordId),
        lastName: rawPatient.lastName,
        firstName: rawPatient.firstName,
        dateOfBirth: dateOfBirth,
        age: patient.age || patient.__age || '',
        gender: gender,
        phone: rawPatient.phone,
        email: email,
        lastVisit: formatDate(patient.last_visit || patient.__lastVisit || ''),
        amka: rawPatient.amka,
        am: formatFieldValue(patient.am ?? patient.__am ?? ''),
        diagnosis: rawPatient.diagnosis,
        address: rawPatient.address,
        notes: rawPatient.notes,
        allergies: formatFieldValue(patient.allergies ?? null),
        death: formatFieldValue(patient.death ?? patient.__death ?? ''),
        created: formatDate(patient.__cardDate ?? patient.card_date ?? patient.datekarta ?? patient.created_at ?? patient.__createdAt ?? ''),
        updated: formatDate(patient.updated_at || patient.__updatedAt || ''),
        raw: rawPatient,
      };
    },
    []
  );


  const handleEditClick = useCallback(
    (event, id) => {
      event.stopPropagation();
      setError('');
      setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
    },
    [setError]
  );

  const handleSaveClick = useCallback((event, id) => {
    event.stopPropagation();
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View } }));
  }, []);

  const handleCancelClick = useCallback(
    (event, id) => {
      event.stopPropagation();
      setError('');
      setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View, ignoreModifications: true } }));
    },
    [setError]
  );

  const handleDeleteClick = useCallback((event, patient) => {
    event.stopPropagation();
    // Only allow admins to delete patients
    if (!isAdmin) {
      alert('Only administrators can delete patients.');
      return;
    }
    setPatientToDelete(patient);
    setDeleteConfirmOpen(true);
  }, [isAdmin]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!patientToDelete || !apiClient) {
      setDeleteConfirmOpen(false);
      return;
    }

    const patientId = patientToDelete.id ?? patientToDelete.__recordId;
    if (!patientId) {
      setError('Invalid patient ID');
      setDeleteConfirmOpen(false);
      return;
    }

    try {
      await deletePatient(apiClient, patientId);
      setPatients(prev => prev.filter(p => p.id !== patientId && p.__recordId !== patientId));
      setError('');
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please sign in again.');
        return;
      }
      setError(err.response?.data?.error ?? err.message ?? 'Failed to delete patient');
    } finally {
      setDeleteConfirmOpen(false);
      setPatientToDelete(null);
    }
  }, [patientToDelete, apiClient]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false);
    setPatientToDelete(null);
  }, []);

  // processRowUpdate and handleProcessRowUpdateError functions have been removed as they were not being used

  const filteredPatients = useMemo(() => {
    const term = normalizeGreekSearch(searchTerm);
    if (!term) {
      return patients;
    }

    return patients.filter((patient) => {
      const values = [
        patient.__displayName,
        patient.__diagnosis,
        patient.diagnosis,
        patient.__phone,
        patient.phone,
        patient.__email,
        patient.email,
        patient.__amka,
        patient.amka,
        patient.__am,
        patient.am,
        patient.__address,
        patient.address,
        patient.id,
        patient.__recordId,
      ];

      return values.some((value) => {
        if (!value) {
          return false;
        }
        const formatted = formatFieldValue(value);
        if (formatted === '—') {
          return false;
        }
        return normalizeGreekSearch(formatted).includes(term);
      });
    });
  }, [patients, searchTerm]);

  const sortedPatients = useMemo(() => {
    const resolveNumericId = (patient) => {
      const raw =
        patient.__recordId ??
        patient.id ??
        patient.ID_PT ??
        patient.id_pt ??
        patient.__internalId ??
        null;

      const numeric = typeof raw === 'string' ? Number(raw) : Number(raw);
      return Number.isFinite(numeric) ? numeric : null;
    };

    const resolveComparable = (patient, index) => {
      const numericId = resolveNumericId(patient);
      if (numericId !== null) {
        return { numericId, index };
      }
      return { numericId: Number.POSITIVE_INFINITY, index };
    };

    return filteredPatients.slice().sort((a, b) => {
      const left = resolveComparable(a, patients.indexOf(a));
      const right = resolveComparable(b, patients.indexOf(b));
      if (left.numericId !== right.numericId) {
        return left.numericId - right.numericId;
      }
      return left.index - right.index;
    });
  }, [filteredPatients, patients]);

  useEffect(() => {
    setCardPage(0);
    // Reset selected patient when page size changes to avoid inconsistencies
    setSelectedPatientId(null);
  }, [cardPageSize, sortedPatients.length]);

  const totalCardPages = useMemo(() => {
    if (!sortedPatients.length) {
      return 1;
    }
    return Math.max(1, Math.ceil(sortedPatients.length / cardPageSize));
  }, [sortedPatients.length, cardPageSize]);

  useEffect(() => {
    setCardPage((prev) => {
      if (prev >= totalCardPages) {
        return totalCardPages - 1;
      }
      return prev;
    });
  }, [totalCardPages]);

  const pagedPatients = useMemo(() => {
    const start = cardPage * cardPageSize;
    const end = start + cardPageSize;
    return sortedPatients.slice(start, end);
  }, [sortedPatients, cardPage, cardPageSize]);

  useEffect(() => {
    setDetailPage(0);
    // Reset selected patient when page size changes to avoid inconsistencies
    setSelectedPatientId(null);
  }, [detailPageSize, sortedPatients.length]);

  const totalDetailPages = useMemo(() => {
    if (!sortedPatients.length) {
      return 1;
    }
    return Math.max(1, Math.ceil(sortedPatients.length / detailPageSize));
  }, [sortedPatients.length, detailPageSize]);

  useEffect(() => {
    setDetailPage((prev) => {
      if (prev >= totalDetailPages) {
        return totalDetailPages - 1;
      }
      return prev;
    });
  }, [totalDetailPages]);

  const pagedDetailPatients = useMemo(() => {
    const start = detailPage * detailPageSize;
    const end = start + detailPageSize;
    return sortedPatients.slice(start, end);
  }, [sortedPatients, detailPage, detailPageSize]);

  useEffect(() => {
    if (!filteredPatients.length) {
      setSelectedPatientId(null);
      return;
    }

    setSelectedPatientId((prev) => {
      if (prev && filteredPatients.some((patient) => patient.id === prev)) {
        return prev;
      }
      // For cards and detail view, try to select the first patient on the current page
      if (viewMode === 'cards') {
        const firstCardPatient = pagedPatients[0];
        return firstCardPatient?.id ?? filteredPatients[0].id;
      } else if (viewMode === 'detail') {
        const firstDetailPatient = pagedDetailPatients[0];
        return firstDetailPatient?.id ?? filteredPatients[0].id;
      }
      return filteredPatients[0].id;
    });
  }, [filteredPatients, viewMode, sortedPatients, cardPageSize, detailPageSize, pagedPatients, pagedDetailPatients]);

  const selectedPatient = useMemo(
    () => patients.find((patient) => patient.id === selectedPatientId) ?? null,
    [patients, selectedPatientId]
  );

  const selectedPatientEntries = useMemo(() => selectedPatient?.__detailEntries ?? [], [selectedPatient]);

  const handleSelectPatient = useCallback((patient, { openDialog = false } = {}) => {
    if (!patient) {
      return;
    }

    const patientId = patient.id ?? patient.__internalId ?? getPatientId(patient) ?? null;
    setSelectedPatientId(patientId);
    if (openDialog) {
      setDetailOpen(true);
    }
    
    // Ensure the selected patient is visible in the current page for both card and detail views
    if (viewMode === 'cards') {
      const patientIndex = sortedPatients.findIndex(p => 
        (p.id ?? p.__internalId ?? getPatientId(p)) === patientId
      );
      if (patientIndex >= 0) {
        const targetPage = Math.floor(patientIndex / cardPageSize);
        if (targetPage !== cardPage) {
          setCardPage(targetPage);
        }
      }
    } else if (viewMode === 'detail') {
      const patientIndex = sortedPatients.findIndex(p => 
        (p.id ?? p.__internalId ?? getPatientId(p)) === patientId
      );
      if (patientIndex >= 0) {
        const targetPage = Math.floor(patientIndex / detailPageSize);
        if (targetPage !== detailPage) {
          setDetailPage(targetPage);
        }
      }
    }
  }, [setSelectedPatientId, viewMode, cardPageSize, cardPage, detailPageSize, detailPage, sortedPatients]);
  
  const handlePrintPatient = useCallback(() => {
    if (!selectedPatient) return;

    const displayName = selectedPatient.__displayName || 'Patient Record';
    const entries = selectedPatient?.__detailEntries || [];

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setError('Pop-up blocked. Please allow pop-ups to print.');
      return;
    }
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Patient Record - ${displayName}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; padding: 40px; line-height: 1.5; color: #333; }
            .header { border-bottom: 2px solid #1976d2; margin-bottom: 30px; padding-bottom: 15px; display: flex; justify-content: space-between; align-items: flex-end; }
            .header h1 { margin: 0; color: #1976d2; font-size: 28px; }
            .header .date { color: #666; font-size: 14px; }
            .section { display: grid; grid-template-columns: 1fr 1fr; gap: 20px 40px; }
            .field { margin-bottom: 15px; break-inside: avoid; }
            .field.full-width { grid-column: span 2; }
            .label { font-weight: 600; color: #757575; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
            .value { font-size: 15px; padding-bottom: 4px; border-bottom: 1px solid #eee; min-height: 1.5em; }
            .footer { margin-top: 50px; font-size: 11px; color: #9e9e9e; border-top: 1px solid #eeeeee; padding-top: 15px; text-align: center; }
            @media print {
              body { padding: 20px; }
              .header { margin-bottom: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1>${displayName}</h1>
              <p style="margin: 4px 0 0; color: #757575;">{t('Patient Medical Record')}</p>
            </div>
            <div class="date">Printed: ${new Date().toLocaleString('el-GR')}</div>
          </div>
          <div class="section">
            ${entries.filter(e => e.label !== 'Full Name').map(e => `
              <div class="field ${e.multiline ? 'full-width' : ''}">
                <div class="label">${e.label}</div>
                <div class="value">${e.value || '—'}</div>
              </div>
            `).join('')}
          </div>
          <div class="footer">
            Confidential Electronic Medical Record - MySurgeon EMR
          </div>
          <script>
            window.onload = () => {
              setTimeout(() => {
                window.print();
                window.onafterprint = () => window.close();
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  }, [selectedPatient]);

  const handleEditSubmit = async (values, formikHelpers) => {
    if (!selectedPatientId) {
      formikHelpers.setStatus('No patient selected.');
      formikHelpers.setSubmitting(false);
      return;
    }

    if (!apiClient) {
      formikHelpers.setStatus('You must be signed in to edit patient records.');
      formikHelpers.setSubmitting(false);
      return;
    }

    const recordId = selectedPatient?.__recordId ?? selectedPatient?.__originalId ?? selectedPatientId;
    const requestId = recordId ? String(recordId) : null;

    if (!requestId) {
      formikHelpers.setStatus('This patient does not have an editable identifier.');
      formikHelpers.setSubmitting(false);
      return;
    }

    const payload = buildPatientUpdatePayload(values);

    const attemptUpdate = async (client) => {
      const updated = await updatePatient(client, requestId, payload);
      let normalizedPatient = null;
      setPatients((prev) => {
        const next = [...prev];
        const index = next.findIndex((patient) => patient.id === requestId || patient.__recordId === requestId);
        normalizedPatient = normalizePatient(updated, index >= 0 ? index : next.length, requestId);
        if (index >= 0) {
          next[index] = normalizedPatient;
        } else {
          next.push(normalizedPatient);
        }
        return next;
      });
      if (normalizedPatient?.id) {
        setSelectedPatientId(normalizedPatient.id);
      }
      setError('');
      return normalizedPatient ?? updated;
    };

    setEditSubmitting(true);
    formikHelpers.setStatus(undefined);

    try {
      await attemptUpdate(apiClient);
      setEditOpen(false);
    } catch (err) {
      if (err.response?.status === 401) {
        const message = 'Session expired. Please sign in again.';
        formikHelpers.setStatus(message);
        setError(message);
        return;
      }

      const details = err.response?.data?.details;
      let message = err.response?.data?.error ?? err.message ?? 'Failed to save changes';

      if (err.response?.data?.error === 'Validation failed' && Array.isArray(details)) {
        const fieldErrors = details.map(d => `${d.path[d.path.length - 1]}: ${d.message}`).join(', ');
        message = `${t('Validation failed')}: ${fieldErrors}`;
      }

      formikHelpers.setStatus(message);
      setError(message);
    } finally {
      setEditSubmitting(false);
      formikHelpers.setSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: 'idPt', headerName: t('ID'), width: 140, valueFormatter: ({ value }) => formatFieldValue(value) },
      {
        field: 'lastName',
        headerName: t('Last Name'),
        flex: 1,
        minWidth: 160,
        editable: true,
        valueFormatter: ({ value }) => formatFieldValue(value),
        renderCell: (params) => (
          <Button
            variant="text"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              handleViewPatient(params.row.raw);
            }}
          >
            {formatFieldValue(params.value)}
          </Button>
        ),
      },
      {
        field: 'firstName',
        headerName: t('First Name'),
        flex: 1,
        minWidth: 160,
        editable: true,
        valueFormatter: ({ value }) => formatFieldValue(value),
        renderCell: (params) => (
          <Button
            variant="text"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              handleViewPatient(params.row.raw);
            }}
          >
            {formatFieldValue(params.value)}
          </Button>
        ),
      },
      {
        field: 'amka',
        headerName: t('AMKA'),
        width: 180,
        editable: true,
        valueFormatter: ({ value }) => formatFieldValue(value),
      },
      { field: 'age', headerName: t('Age'), width: 110, valueFormatter: ({ value }) => formatFieldValue(value) },
      { field: 'dateOfBirth', headerName: t('Date of Birth'), width: 160, valueFormatter: ({ value }) => formatFieldValue(value) },
      { field: 'gender', headerName: t('Gender'), width: 120, valueFormatter: ({ value }) => formatFieldValue(value) },
      { field: 'created', headerName: t('Card Date'), width: 180, valueFormatter: ({ value }) => formatFieldValue(value) },
      {
        field: 'phone',
        headerName: t('Phone'),
        width: 180,
        editable: true,
        valueFormatter: ({ value }) => formatFieldValue(value),
      },
      { field: 'diagnosis', headerName: t('Diagnosis'), flex: 1, minWidth: 220, valueFormatter: ({ value }) => formatFieldValue(value) },
      { field: 'email', headerName: t('Email'), width: 220, valueFormatter: ({ value }) => formatFieldValue(value) },
      { field: 'am', headerName: t('AM'), width: 140, valueFormatter: ({ value }) => formatFieldValue(value) },
      { field: 'address', headerName: t('Address'), flex: 1, minWidth: 220, valueFormatter: ({ value }) => formatFieldValue(value) },
      { field: 'notes', headerName: t('Notes'), flex: 1, minWidth: 220, valueFormatter: ({ value }) => formatFieldValue(value) },
      { field: 'allergies', headerName: t('Allergies'), flex: 1, minWidth: 200, valueFormatter: ({ value }) => formatFieldValue(value) },
      { field: 'death', headerName: t('Death'), width: 160, valueFormatter: ({ value }) => formatFieldValue(value) },
      { field: 'lastVisit', headerName: t('Last Visit'), width: 180, valueFormatter: ({ value }) => formatFieldValue(value) },
      { field: 'updated', headerName: t('Updated'), width: 180, valueFormatter: ({ value }) => formatFieldValue(value) },
      {
        field: 'actions',
        headerName: t('Actions'),
        width: 180,
        sortable: false,
        renderCell: (params) => {
          const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
          const patient = params.row.raw;

          if (isInEditMode) {
            return (
              <Stack direction="row" spacing={1}>
                <Tooltip title="Save">
                  <span>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={(event) => handleSaveClick(event, params.id)}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Cancel">
                  <span>
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={(event) => handleCancelClick(event, params.id)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            );
          }

          return (
            <Stack direction="row" spacing={1}>
              {canWrite && (
                <Tooltip title="Edit inline">
                  <span>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={(event) => handleEditClick(event, params.id)}
                      disabled={!patient}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="View details">
                <span>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleSelectPatient(patient, { openDialog: true });
                    }}
                    disabled={!patient}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isAdmin ? t('Delete') : t('Only administrators can delete patients')}>
                <span>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={(event) => handleDeleteClick(event, patient)}
                    disabled={!patient || !isAdmin}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [handleCancelClick, handleEditClick, handleSaveClick, handleSelectPatient, handleDeleteClick, rowModesModel, handleViewPatient, isAdmin, canWrite]
  );


  const rows = useMemo(
    () => {
      console.log('Building rows from sortedPatients:', sortedPatients);
      const builtRows = sortedPatients.map((patient, index) => {
        const row = buildRowFromPatient(patient, index);
        console.log(`Built row ${index}:`, row);
        return row;
      });
      console.log('Final rows array:', builtRows);
      return builtRows;
    },
    [sortedPatients, buildRowFromPatient]
  );

  const totalPatients = patients.length;
  const filteredCount = filteredPatients.length;
  const hasPatients = filteredCount > 0;
  const patientCountLabel = totalPatients > 0
    ? `${filteredCount} / ${totalPatients} ${t('patients')}`
    : t('No patients');
  const hasSearch = Boolean(searchTerm.trim());

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-end', md: 'center' }} mb={4} spacing={2}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, background: 'linear-gradient(45deg, #1976d2, #64b5f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {t('Patient Records')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
            {t('Results Found')}: <strong>{filteredCount}/{totalPatients}</strong> {t('patients')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DashboardIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{ minWidth: 120 }}
          >
            {t('Dashboard')}
          </Button>
        </Box>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, next) => {
            if (next) {
              setViewMode(next);
              if (next === 'detail') {
                setDetailPage(0);
              }
            }
          }}
          size="small"
        >
          <ToggleButton value="table">{t('Table')}</ToggleButton>
          <ToggleButton value="cards">{t('Cards')}</ToggleButton>
          <ToggleButton value="detail">{t('Form View')}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={3}>
                <Typography variant="h6">{t('Patient Records')}</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} width={{ xs: '100%', sm: 'auto' }}>
                  <TextField
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    inputRef={searchInputRef}
                    placeholder={t('Search patients by name...')}
                    size="small"
                    sx={{ minWidth: { xs: '100%', sm: 280 }, flex: { xs: 1, sm: 'unset' } }}
                    InputProps={{
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearchTerm('')}
                            edge="end"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {canWrite && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleCreateOpen}
                      disabled={!isAuthenticated || createSubmitting}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      {t('Add Patient')}
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<TableViewIcon />}
                    onClick={() => navigate('/query?tab=batch')}
                    sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    {t('Batch Diagnosis Lookup')}
                  </Button>
                </Stack>
              </Stack>
              {!isAuthenticated && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('Sign in to view patient records.')}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {viewMode === 'table' && (
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {patientCountLabel}
                      </Typography>
                      <Box sx={{ height: { xs: 500, md: 620 }, width: '100%', overflowX: 'auto' }}>
                        <DataGrid
                          key={`page-${paginationModel.page}-${paginationModel.pageSize}`}
                          rows={rows}
                          columns={columns}
                          pageSizeOptions={[10, 25, 50, 100]}
                          paginationModel={paginationModel}
                          onPaginationModelChange={setPaginationModel}
                          pagination
                          rowCount={totalCount}
                          slots={{
                            pagination: CustomPagination,
                            toolbar: CustomPagination,
                          }}
                          disableRowSelectionOnClick
                          onRowClick={(params) => handleViewPatient(params.row.raw)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Box>
                    </Stack>
                  )}
                  {viewMode === 'detail' && (
                    hasPatients ? (
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Paper
                          variant="outlined"
                          sx={{
                            width: { xs: '100%', md: 320 },
                            maxHeight: 800,
                            minHeight: 360,
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <Box px={2} py={1} flexShrink={0}>
                            <Typography variant="subtitle2" color="text.secondary">
                              {patientCountLabel}
                            </Typography>
                          </Box>
                          <Divider sx={{ mb: 0 }} />
                          <Box px={1} py={0.5} flexShrink={0}>
                            <Stack direction="row" spacing={0} alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'nowrap', overflow: 'hidden' }}>
                              <FormControl size="small" sx={{ minWidth: 45 }}>
                                <Select
                                  value={detailPageSize}
                                  onChange={(event) => {
                                    setDetailPageSize(Number(event.target.value));
                                  }}
                                  variant="standard"
                                  sx={{ fontSize: '0.75rem' }}
                                >
                                  {[10, 25, 50, 100].map((size) => (
                                    <MenuItem key={size} value={size}>
                                      {size}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              {totalDetailPages > 1 && (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <IconButton 
                                    onClick={() => {
                                      setDetailPage(0);
                                      if (sortedPatients.length > 0) {
                                        const firstPatient = sortedPatients[0];
                                        const patientId = firstPatient.id ?? firstPatient.__internalId ?? getPatientId(firstPatient);
                                        if (patientId) setSelectedPatientId(patientId);
                                      }
                                    }}
                                    disabled={detailPage === 0}
                                    size="small"
                                  >
                                    <FirstPageIcon fontSize="inherit" />
                                  </IconButton>
                                  <Pagination
                                    count={totalDetailPages}
                                    page={detailPage + 1}
                                    onChange={(_, value) => {
                                      const newPage = value - 1;
                                      setDetailPage(newPage);
                                      const start = newPage * detailPageSize;
                                      if (sortedPatients.length > start) {
                                        const firstPatient = sortedPatients[start];
                                        const patientId = firstPatient.id ?? firstPatient.__internalId ?? getPatientId(firstPatient);
                                        if (patientId) setSelectedPatientId(patientId);
                                      }
                                    }}
                                    color="primary"
                                    size="small"
                                    siblingCount={1}
                                    boundaryCount={0}
                                    sx={{
                                      '& .MuiPaginationItem-root': {
                                        minWidth: 22,
                                        height: 22,
                                        fontSize: '0.7rem',
                                        margin: '0 1px',
                                        padding: '0 2px'
                                      }
                                    }}
                                  />
                                  <IconButton 
                                    onClick={() => {
                                      const lastPage = totalDetailPages - 1;
                                      setDetailPage(lastPage);
                                      const start = lastPage * detailPageSize;
                                      if (sortedPatients.length > start) {
                                        const firstPatient = sortedPatients[start];
                                        const patientId = firstPatient.id ?? firstPatient.__internalId ?? getPatientId(firstPatient);
                                        if (patientId) setSelectedPatientId(patientId);
                                      }
                                    }}
                                    disabled={detailPage >= totalDetailPages - 1}
                                    size="small"
                                  >
                                    <LastPageIcon fontSize="inherit" />
                                  </IconButton>
                                </Stack>
                              )}
                            </Stack>
                          </Box>
                          <Divider sx={{ mb: 0 }} />
                          <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            <List disablePadding>
                              {pagedDetailPatients.map((patient, index) => {
                                const key =
                                  patient.id ??
                                  patient.__recordId ??
                                  patient.__internalId ??
                                  `detail-${detailPage}-${index}`;
                                const displayName = formatFieldValue(patient.__displayName);
                                const displayId = formatFieldValue(
                                  patient.id ??
                                    patient.__recordId ??
                                    patient.ID_PT ??
                                    patient.id_pt ??
                                    patient.__internalId ??
                                    null
                                );
                                const primaryText = displayId !== '—' ? `${displayName} (ID: ${displayId})` : displayName;
                                const phoneValue = formatFieldValue(patient.__phone ?? patient.phone);
                                const emailValue = formatFieldValue(patient.__email ?? patient.email);
                                const secondaryText = phoneValue !== '—' ? phoneValue : emailValue;
                              const ageValue = formatFieldValue(patient.__age ?? patient.age);
                              const diagnosisValue = formatFieldValue(patient.__diagnosis ?? patient.diagnosis);
                                const listPatientId =
                                  getPatientId(patient) ??
                                  patient.id ??
                                  patient.__recordId ??
                                  patient.__internalId ??
                                  null;
                                const isSelected =
                                  listPatientId !== null && selectedPatientId !== null
                                    ? String(listPatientId) === String(selectedPatientId)
                                    : false;

                                return (
                                  <Fragment key={key}>
                                    <ListItemButton
                                      selected={isSelected}
                                      onClick={() => {
                                        handleSelectPatient(patient);
                                      }}
                                    >
                                      <ListItemText
                                        primary={primaryText}
                                        secondary={
                                          <Box>
                                            {secondaryText !== '—' && (
                                              <Typography variant="body2">{secondaryText}</Typography>
                                            )}
                                            {ageValue !== '—' && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Age: {ageValue}
                                              </Typography>
                                            )}
                                            {diagnosisValue !== '—' && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Diagnosis: {diagnosisValue}
                                              </Typography>
                                            )}
                                          </Box>
                                        }
                                      />
                                    </ListItemButton>
                                    {index < pagedDetailPatients.length - 1 && <Divider component="li" />}
                                  </Fragment>
                                );
                              })}
                            </List>
                          </Box>
                          <Divider sx={{ mt: 0 }} />
                          <Box px={1} py={0.5} flexShrink={0}>
                            <Stack direction="row" spacing={0} alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'nowrap', overflow: 'hidden' }}>
                              <FormControl size="small" sx={{ minWidth: 45 }}>
                                <Select
                                  value={detailPageSize}
                                  onChange={(event) => {
                                    setDetailPageSize(Number(event.target.value));
                                  }}
                                  variant="standard"
                                  sx={{ fontSize: '0.75rem' }}
                                >
                                  {[10, 25, 50, 100].map((size) => (
                                    <MenuItem key={size} value={size}>
                                      {size}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              {totalDetailPages > 1 && (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <IconButton 
                                    onClick={() => {
                                      setDetailPage(0);
                                      if (sortedPatients.length > 0) {
                                        const firstPatient = sortedPatients[0];
                                        const patientId = firstPatient.id ?? firstPatient.__internalId ?? getPatientId(firstPatient);
                                        if (patientId) setSelectedPatientId(patientId);
                                      }
                                    }}
                                    disabled={detailPage === 0}
                                    size="small"
                                  >
                                    <FirstPageIcon fontSize="inherit" />
                                  </IconButton>
                                  <Pagination
                                    count={totalDetailPages}
                                    page={detailPage + 1}
                                    onChange={(_, value) => {
                                      const newPage = value - 1;
                                      setDetailPage(newPage);
                                      const start = newPage * detailPageSize;
                                      if (sortedPatients.length > start) {
                                        const firstPatient = sortedPatients[start];
                                        const patientId = firstPatient.id ?? firstPatient.__internalId ?? getPatientId(firstPatient);
                                        if (patientId) setSelectedPatientId(patientId);
                                      }
                                    }}
                                    color="primary"
                                    size="small"
                                    siblingCount={1}
                                    boundaryCount={0}
                                    sx={{
                                      '& .MuiPaginationItem-root': {
                                        minWidth: 22,
                                        height: 22,
                                        fontSize: '0.7rem',
                                        margin: '0 1px',
                                        padding: '0 2px'
                                      }
                                    }}
                                  />
                                  <IconButton 
                                    onClick={() => {
                                      const lastPage = totalDetailPages - 1;
                                      setDetailPage(lastPage);
                                      const start = lastPage * detailPageSize;
                                      if (sortedPatients.length > start) {
                                        const firstPatient = sortedPatients[start];
                                        const patientId = firstPatient.id ?? firstPatient.__internalId ?? getPatientId(firstPatient);
                                        if (patientId) setSelectedPatientId(patientId);
                                      }
                                    }}
                                    disabled={detailPage >= totalDetailPages - 1}
                                    size="small"
                                  >
                                    <LastPageIcon fontSize="inherit" />
                                  </IconButton>
                                </Stack>
                              )}
                            </Stack>
                          </Box>
                        </Paper>
                        <Paper
                          variant="outlined"
                          sx={{
                            flex: 1,
                            p: 2,
                            minHeight: 320,
                          }}
                        >
                          {selectedPatient ? (
                            <Stack spacing={2}>
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                              >
                                <Typography 
                                  variant="subtitle1" 
                                  fontWeight={600}
                                  sx={{ 
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    wordBreak: 'break-word',
                                    lineHeight: 1.2
                                  }}
                                >
                                  {selectedPatient.__displayName}
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                  <Button
                                    variant="outlined"
                                    startIcon={<PrintIcon />}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handlePrintPatient();
                                    }}
                                    size="small"
                                  >
                                    Print
                                  </Button>
                                  <Button
                                    variant="contained"
                                    startIcon={<EditIcon />}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleViewPatient(selectedPatient);
                                    }}
                                    size="small"
                                  >
                                    Open Patient
                                  </Button>
                                </Stack>
                              </Stack>
                              <Grid container spacing={2}>
                                {selectedPatientEntries.map((entry) => (
                                  <Grid item xs={12} sm={entry.multiline ? 12 : 6} key={entry.key}>
                                    <TextField
                                      label={entry.label}
                                      value={entry.value}
                                      fullWidth
                                      multiline={entry.multiline}
                                      minRows={entry.multiline ? 3 : 1}
                                      InputProps={{ readOnly: true }}
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                              <Typography variant="caption" color="text.secondary">
                                ID: {selectedPatient.__recordId ?? selectedPatient.id ?? '—'}
                              </Typography>
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t('Select a patient')}
                            </Typography>
                          )}
                        </Paper>
                      </Stack>
                    ) : (
                      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body1">
                          {hasSearch ? t('No patients match the search.') : t('No patients available yet.')}
                        </Typography>
                      </Paper>
                    )
                  )}
                  {viewMode === 'cards' && (
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        spacing={2}
                      >
                        <Typography variant="subtitle2" color="text.secondary">
                          {patientCountLabel}
                        </Typography>
                        {hasPatients && (
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                              <InputLabel id="card-page-size-label-top">{t('Cards per page')}</InputLabel>
                              <Select
                                labelId="card-page-size-label-top"
                                value={cardPageSize}
                                label={t('Cards per page')}
                                onChange={(event) => {
                                  setCardPageSize(Number(event.target.value));
                                }}
                              >
                                {[10, 25, 50, 100].map((size) => (
                                  <MenuItem key={size} value={size}>
                                    {size}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {totalCardPages > 1 && (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Tooltip title="First page">
                                  <IconButton 
                                    onClick={() => {
                                      setCardPage(0);
                                      // Auto-select first patient on new page
                                      if (sortedPatients.length > 0) {
                                        const firstPatient = sortedPatients[0];
                                        const patientId = firstPatient.id ?? firstPatient.__internalId ?? getPatientId(firstPatient);
                                        if (patientId) {
                                          setSelectedPatientId(patientId);
                                        }
                                      }
                                    }}
                                    disabled={cardPage === 0}
                                    size="small"
                                  >
                                    <FirstPageIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Pagination
                                  count={totalCardPages}
                                  page={cardPage + 1}
                                  onChange={(_, value) => {
                                    const newPage = value - 1;
                                    setCardPage(newPage);
                                    // Auto-select first patient on new page
                                    const start = newPage * cardPageSize;
                                    if (sortedPatients.length > start) {
                                      const firstPatient = sortedPatients[start];
                                      const patientId = firstPatient.id ?? firstPatient.__internalId ?? getPatientId(firstPatient);
                                      if (patientId) {
                                        setSelectedPatientId(patientId);
                                      }
                                    }
                                  }}
                                  color="primary"
                                  siblingCount={1}
                                  boundaryCount={1}
                                />
                                <Tooltip title="Last page">
                                  <IconButton 
                                    onClick={() => {
                                      const lastPage = totalCardPages - 1;
                                      setCardPage(lastPage);
                                      // Auto-select first patient on new page
                                      const start = lastPage * cardPageSize;
                                      if (sortedPatients.length > start) {
                                        const firstPatient = sortedPatients[start];
                                        const patientId = firstPatient.id ?? firstPatient.__internalId ?? getPatientId(firstPatient);
                                        if (patientId) {
                                          setSelectedPatientId(patientId);
                                        }
                                      }
                                    }}
                                    disabled={cardPage >= totalCardPages - 1}
                                    size="small"
                                  >
                                    <LastPageIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            )}
                          </Stack>
                        )}
                      </Stack>
                      <Box sx={{ maxHeight: 720, minHeight: 360, overflowY: 'auto', pr: 1 }}>
                        {hasPatients ? (
                          <Grid container spacing={2}>
                            {pagedPatients.map((patient, index) => (
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                xl={3}
                                key={patient.id ?? patient.__recordId ?? patient.__internalId ?? `card-${index}`}
                              >
                                <PatientCard
                                  key={patient.id ?? patient.__recordId ?? `card-${index}`}
                                  patient={patient}
                                  onView={() => handleSelectPatient(patient, { openDialog: true })}
                                  onNavigate={() => handleViewPatient(patient)}
                                  onEdit={() => handleEditPatient(patient)}
                                  onDelete={(patient) => {
                                    setPatientToDelete(patient);
                                    setDeleteConfirmOpen(true);
                                  }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1">
                              {totalPatients === 0
                                ? 'No patients available yet.'
                                : hasSearch
                                ? 'No patients match the search.'
                                : 'No patients available yet.'}
                            </Typography>
                          </Paper>
                        )}
                      </Box>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        spacing={2}
                        mt={2}
                      >
                        <Typography variant="subtitle2" color="text.secondary">
                          {patientCountLabel}
                        </Typography>
                        {hasPatients && (
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                              <InputLabel id="card-page-size-label-bottom">{t('Cards per page')}</InputLabel>
                              <Select
                                labelId="card-page-size-label-bottom"
                                value={cardPageSize}
                                label={t('Cards per page')}
                                onChange={(event) => {
                                  setCardPageSize(Number(event.target.value));
                                }}
                              >
                                {[10, 25, 50, 100].map((size) => (
                                  <MenuItem key={size} value={size}>
                                    {size}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {totalCardPages > 1 && (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Tooltip title="First page">
                                  <IconButton 
                                    onClick={() => {
                                      setCardPage(0);
                                      // Auto-select first patient on new page
                                      if (sortedPatients.length > 0) {
                                        const firstPatient = sortedPatients[0];
                                        const patientId = firstPatient.id ?? firstPatient.__internalId ?? getPatientId(firstPatient);
                                        if (patientId) {
                                          setSelectedPatientId(patientId);
                                        }
                                      }
                                    }}
                                    disabled={cardPage === 0}
                                    size="small"
                                  >
                                    <FirstPageIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Pagination
                                  count={totalCardPages}
                                  page={cardPage + 1}
                                  onChange={(_, value) => {
                                    const newPage = value - 1;
                                    setCardPage(newPage);
                                    // Auto-select first patient on new page
                                    const start = newPage * cardPageSize;
                                    if (sortedPatients.length > start) {
                                      const firstPatient = sortedPatients[start];
                                      const patientId = firstPatient.id ?? firstPatient.__internalId ?? getPatientId(firstPatient);
                                      if (patientId) {
                                        setSelectedPatientId(patientId);
                                      }
                                    }
                                  }}
                                  color="primary"
                                  siblingCount={1}
                                  boundaryCount={1}
                                />
                                <Tooltip title="Last page">
                                  <IconButton 
                                    onClick={() => {
                                      const lastPage = totalCardPages - 1;
                                      setCardPage(lastPage);
                                      // Auto-select first patient on new page
                                      const start = lastPage * cardPageSize;
                                      if (sortedPatients.length > start) {
                                        const firstPatient = sortedPatients[start];
                                        const patientId = firstPatient.id ?? firstPatient.__internalId ?? getPatientId(firstPatient);
                                        if (patientId) {
                                          setSelectedPatientId(patientId);
                                        }
                                      }
                                    }}
                                    disabled={cardPage >= totalCardPages - 1}
                                    size="small"
                                  >
                                    <LastPageIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            )}
                          </Stack>
                        )}
                      </Stack>
                    </Stack>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <PatientDetailDialog open={detailOpen} patient={selectedPatient} onClose={handleDetailClose} />
      <PatientEditDialog
        open={editOpen}
        patient={selectedPatient}
        onClose={handleEditClose}
        onSubmit={handleEditSubmit}
        onDelete={(patient) => {
          setPatientToDelete(patient);
          setEditOpen(false);
          setDeleteConfirmOpen(true);
        }}
        submitting={editSubmitting}
      />
      <PatientEditDialog
        open={createOpen}
        patient={null}
        onClose={handleCreateClose}
        onSubmit={handleCreateSubmit}
        submitting={createSubmitting}
        error={createError}
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">{t('Delete Patient')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('Are you sure you want to delete patient')} {patientToDelete?.raw?.firstName} {patientToDelete?.raw?.lastName}? {t('This action cannot be undone.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            {t('Cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function CustomPagination() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, (state) => state.pagination.paginationModel.page);
    const pageSize = useGridSelector(apiRef, (state) => state.pagination.paginationModel.pageSize);
  const rowCount = useGridSelector(apiRef, (state) => state.rows.totalRowCount) || 0;

  const handleFirstPageButtonClick = () => {
    apiRef.current.setPage(0);
  };

  const handleLastPageButtonClick = () => {
    apiRef.current.setPage(Math.max(0, Math.ceil(rowCount / pageSize) - 1));
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', p: 1 }}>
      <Tooltip title="First page">
        <span>
          <IconButton
            onClick={handleFirstPageButtonClick}
            disabled={page === 0}
            aria-label="first page"
            size="small"
          >
            <FirstPageIcon />
          </IconButton>
        </span>
      </Tooltip>
      <TablePagination
        component="div"
        count={rowCount}
        page={page}
        onPageChange={(event, newPage) => apiRef.current.setPage(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(event) =>
          apiRef.current.setPageSize(Number(event.target.value))
        }
        rowsPerPageOptions={[10, 25, 50, 100]}
        slotProps={{
          select: {
            'aria-label': 'rows per page',
          },
          actions: {
            showFirstButton: false,
            showLastButton: false,
          },
        }}
      />
      <Tooltip title="Last page">
        <span>
          <IconButton
            onClick={handleLastPageButtonClick}
            disabled={page >= Math.ceil(rowCount / pageSize) - 1}
            aria-label="last page"
            size="small"
          >
            <LastPageIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

export default PatientsPage;
